import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../variables/constants';

/**
 * Bandeau qui dit sur quel environnement on travaille.
 *
 * POURQUOI IL EST MONTE DANS App.js ET NON DANS Layout. Il doit se voir sur TOUS
 * les écrans, la page de connexion comprise, et c'est même là qu'il sert le plus :
 * avant d'avoir saisi quoi que ce soit. Or `Layout` est court-circuité sur la
 * connexion, l'OTP et l'activation. Posé là, le bandeau aurait manqué précisément
 * aux écrans qui en ont le plus besoin.
 *
 * POURQUOI `axios` ET NON `axiosInstance`. L'instance porte l'intercepteur qui,
 * sur 401, déconnecte et renvoie vers la page de connexion. Cet appel part AVANT
 * toute authentification : passer par elle aurait fait d'un bandeau un motif de
 * déconnexion. La route `/v1/banniere` est ouverte côté serveur, elle n'a besoin
 * d'aucun jeton.
 *
 * SON ÉCHEC EST SILENCIEUX, ET NE LAISSE RIEN. Un serveur injoignable ne doit
 * jamais afficher un bandeau au hasard : annoncer « TEST » sur la production
 * ferait manipuler des dossiers réels à quelqu'un qui se croit dans un bac à
 * sable. Sans réponse, on n'affiche rien.
 */

/* Les deux teintes de PRODUCTION viennent du MEME logo, echantillonnees sur
   `Poolins/mobile/logo.png` : l'or de l'embleme CEDEAO en fond, le cafe de
   l'anneau « BROWN CARD » pour le texte. Le serveur reel s'annonce donc sous
   les couleurs de l'institution.

   TEST porte le rouge. Il ne signale pas un danger mais une interdiction :
   « ces donnees ne sont pas reelles ».

   LE TEXTE DE PRODUCTION EST SOMBRE, ET C'EST OBLIGATOIRE. Du blanc sur cet or
   tombe a 1,53:1, soit illisible ; le cafe du logo donne 7,05:1. Reprendre par
   habitude le texte clair de l'autre bandeau aurait rendu le message de
   production impossible a lire - celui, precisement, qui annonce que les
   operations sont definitives.

   LES DEUX FONDS SE DISTINGUENT AUSSI L'UN DE L'AUTRE, de 3,25:1, et pas
   seulement de leur texte : un bandeau qu'on confond avec l'autre ne remplit
   pas son office. */
const COULEURS = {
  PRODUCTION: { fond: '#f2ce4c', texte: '#6a2a00' },
  TEST: { fond: '#d32f2f', texte: '#ffffff' },
};

/* Hauteur du bandeau, exposée comme variable CSS pour que la mise en page s'en
   écarte au lieu de passer dessous. La règle qui la consomme est dans
   `theme/variables.css`, la seule feuille que `index.js` importe : posée dans
   `index.css`, que rien n'importe, elle restait morte et le fil d'Ariane
   passait sous la NavBar. */
const HAUTEUR = 34;

/* Vitesse du defilement, en pixels par seconde. Une duree fixe aurait fait
   defiler un texte court lentement et un texte long a toute allure : c'est la
   VITESSE qui doit rester constante, pas le temps d'un tour. */
const VITESSE_PX_S = 55;

export default function BanniereEnvironnement() {
  const [banniere, setBanniere] = useState(null);

  useEffect(() => {
    let ignore = false;
    axios.get(`${API_URL}/v1/banniere`)
      .then((r) => {
        const b = r?.data?.data;
        if (!ignore && b?.texte) setBanniere(b);
      })
      .catch(() => { /* silence volontaire : voir l'en-tête du fichier */ });
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--cb-banniere-h', banniere ? `${HAUTEUR}px` : '0px',
    );
  }, [banniere]);

  /* COMBIEN DE COPIES, ET DE COMBIEN ON TRANSLATE.
     Le bandeau doit rester plein en permanence : le texte ne doit pas attendre
     d'avoir fini de sortir pour que le suivant entre. On mesure donc une copie,
     puis on en pose assez pour couvrir la largeur du bandeau PLUS une, et on
     translate d'exactement UNE copie. A la fin du cycle, chaque copie a pris la
     place de sa voisine : l'image est identique, le retour a zero invisible, et
     il n'y a jamais de vide.

     Forcer chaque copie a la largeur du bandeau, comme je l'avais fait d'abord,
     produisait exactement le defaut inverse : un texte court flottait seul au
     milieu et le suivant n'arrivait qu'apres un long blanc. */
  const [largeurCopie, setLargeurCopie] = useState(0);
  const [copies, setCopies] = useState(2);
  const refConteneur = useRef(null);
  const refMesure = useRef(null);

  useLayoutEffect(() => {
    if (!banniere) return undefined;

    const mesurer = () => {
      const copie = refMesure.current?.offsetWidth ?? 0;
      const conteneur = refConteneur.current?.offsetWidth ?? 0;
      if (!copie || !conteneur) return;
      setLargeurCopie(copie);
      // « + 1 » : il faut toujours une copie de rabe hors champ, sinon un vide
      // apparait a droite juste avant le retour a zero.
      setCopies(Math.ceil(conteneur / copie) + 1);
    };

    mesurer();
    // La largeur disponible change avec la fenetre et avec le repli de la
    // sidebar : sans cette relecture, un bandeau mesure large resterait troue
    // apres un agrandissement.
    window.addEventListener('resize', mesurer);
    return () => window.removeEventListener('resize', mesurer);
  }, [banniere]);

  if (!banniere) return null;

  const palette = COULEURS[banniere.environnement] || COULEURS.PRODUCTION;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: HAUTEUR,
        zIndex: 2000,
        background: palette.fond,
        color: palette.texte,
        display: 'flex',
        alignItems: 'center',
        fontSize: 12.5,
        fontWeight: 600,
        letterSpacing: '.02em',
        /* Le défilement se joue à l'intérieur : le bandeau, lui, garde sa
           hauteur. Sans ce `overflow: hidden`, la piste déborderait sur la
           largeur de la page et provoquerait un défilement horizontal. */
        overflow: 'hidden',
      }}
      title={banniere.texte}
      ref={refConteneur}
    >
      <div
        className="cb-banniere-piste"
        style={largeurCopie ? {
          '--cb-copie-w': `${largeurCopie}px`,
          animationDuration: `${(largeurCopie / VITESSE_PX_S).toFixed(2)}s`,
        } : undefined}
      >
        {/* La première copie porte la mesure ET le message lu à voix haute.
            Les suivantes sont décoratives : `aria-hidden` évite qu'un lecteur
            d'écran ne répète le même avertissement autant de fois qu'il y a de
            copies à l'écran. */}
        <span className="cb-banniere-texte" ref={refMesure}>{banniere.texte}</span>
        {Array.from({ length: Math.max(copies - 1, 1) }, (_, i) => (
          <span className="cb-banniere-texte" aria-hidden="true" key={i}>
            {banniere.texte}
          </span>
        ))}
      </div>
    </div>
  );
}
